import type { MediaItem } from "@gooner/shared";
import { clipKey } from "@gooner/shared";
import { api, userApi } from "./api";
import { useSession } from "./session";
import { syncWallAllowance } from "./wallAllowance";

/**
 * How long each clip is on screen, per SLOT (a region tile, a clip player), buffered
 * and batched to the signed-in user's stats. This is the screen half of what GoonFuel
 * has always done for itself — the mini player measures its own single stream; a wall
 * measures every region at once, so the buffering matters here and the shape follows
 * lib/clipViews.ts, the proven batcher on this exact surface.
 *
 * TWO NUMBERS, TWO LEDGERS. clipViews counts PLAYTHROUGHS (how much a clip was watched,
 * anyone, session or not); this counts SECONDS PER USER (whose taste is this). They ride
 * different tables and answer different questions — do not merge them.
 *
 * WHAT THIS FEEDS, AND WHAT IT DOES NOT. Screen watch events land with surface='screen'
 * and feed the TASTE aggregates (most watched, favorite category, top keywords). They
 * are deliberately kept OUT of the TIME aggregates server-side: a six-region wall
 * consumes six clip-hours per wall-clock hour, and the profile's "time gooned" is the
 * wall-clock, which rides goon_sessions (see !130). So this can be liberal about
 * counting every region without inflating anyone's hours.
 *
 * AND A THIRD DESTINATION: THE RUNNING SESSION. The recap's "most watched" needs seconds
 * per clip for THAT session and for anonymous owners too, neither of which watch_events
 * can answer. While a session runs, every closed interval is also tallied for it
 * (setWatchSession) and sent to /session/watch.
 *
 * SIGNED IN ONLY, CHECKED AT FLUSH. An anonymous wall buffers nothing worth sending —
 * the gate is read when the batch goes out (GoonFuel's rule), so a mid-session sign-in
 * starts counting without a remount.
 *
 * Previews must NOT call this: a hover loop, the ambient backdrop, an editor's source
 * preview are not someone watching. The caller's own discriminator — RegionPlayer only
 * begins a slot where it fires onView, which a synced follower and every preview omit —
 * is what keeps them out, exactly as it keeps them out of clipViews.
 */

// A wall reports every ~30s; short enough that a closed tab loses little, long enough
// that a nine-region wall is two requests a minute, not twenty.
const FLUSH_MS = 30_000;
// A single interval floor, same as GoonFuel's: a clip flashed for a frame during a
// crossfade is not a watch.
const MIN_SECONDS = 0.5;

/** A slot's currently-open interval: the clip, when it began, and whether it ran on a
 *  WALL view (Community / Premium Wall) — which the daily wall allowance never counts. */
const open = new Map<string, { clip: MediaItem; startedAt: number; wall: boolean }>();
/** clipKey-independent buffer: one entry per (clip, closed interval). */
const pending: Array<{ clip: MediaItem; seconds: number; endedAt: number; wall: boolean }> = [];
let timer: ReturnType<typeof setTimeout> | undefined;
/** clipKey -> seconds watched while a session ran, not sent yet. */
const sessionPending = new Map<string, { clip: MediaItem; seconds: number }>();
let sessionOn = false;
let sessionDevice: string | undefined;
/*
 * WHICH VIEW THE SCREEN IS ON, as far as the meter cares: a wall or not. One flag rather
 * than a parameter threaded through RegionTile and RegionPlayer, because exactly one
 * DisplayApp is mounted per page and it is the only thing that knows (viewIdKind). Read
 * at watchBegin, so a slot opened on a wall stays a wall interval even if the screen
 * is steered to a saved view before it closes — the seconds were the wall's.
 */
let onWall = false;
export function setWatchOnWall(wall: boolean): void {
  onWall = wall;
}
/*
 * PAUSE IS NOT WATCHING (2026-09-10). Intervals used to stay open across a pause, which
 * the taste aggregates could shrug at and a meter cannot: a wall paused overnight would
 * have been sixteen tiles of "watching" till morning. A pause banks every open interval
 * and remembers its clip; resume reopens them from now.
 */
const parked = new Map<string, { clip: MediaItem; wall: boolean }>();
export function watchPause(): void {
  const now = Date.now();
  for (const [slot, iv] of open) {
    parked.set(slot, { clip: iv.clip, wall: iv.wall });
    close(slot, now);
  }
}
export function watchResume(): void {
  const now = Date.now();
  for (const [slot, p] of parked) if (!open.has(slot)) open.set(slot, { clip: p.clip, startedAt: now, wall: p.wall });
  parked.clear();
}

/**
 * Close a slot's open interval into the buffer, if it ran long enough to be a watch.
 *
 * WHEN IT ENDED TRAVELS WITH IT. A flush stamps every interval in the batch with one
 * arrival time, and `seconds` alone cannot place an interval against that stamp: three
 * ten-second clips from one region, all flushed together, would each look like the
 * same last ten seconds and count as ten, not thirty. The server's wall-clock measure
 * (the Faps watch threshold) lays intervals end to end by `endedAt` — consecutive
 * clips from one region line up, concurrent regions overlap and count once.
 */
function close(slot: string, at: number): void {
  const iv = open.get(slot);
  if (!iv) return;
  open.delete(slot);
  const seconds = (at - iv.startedAt) / 1000;
  if (seconds < MIN_SECONDS) return;
  pending.push({ clip: iv.clip, seconds, endedAt: at, wall: iv.wall });
  if (sessionOn) {
    const key = clipKey(iv.clip);
    const entry = sessionPending.get(key);
    if (entry) entry.seconds += seconds;
    else sessionPending.set(key, { clip: iv.clip, seconds });
  }
}

/**
 * Send what has accumulated. Fire-and-forget and cleared BEFORE the request, so a
 * failure is lost seconds rather than a double count on the next flush — clipViews'
 * discipline. Gated on a user here, at send time.
 */
function flush(keepalive = false): Promise<unknown> {
  if (timer !== undefined) {
    clearTimeout(timer);
    timer = undefined;
  }
  const session = flushSession(keepalive);
  if (pending.length === 0) return session;
  const batch = pending.splice(0, pending.length);
  if (!useSession.getState().user) return session; // anonymous: drop what we buffered
  // The ledger answers with the day's wall-seconds; the gauge re-syncs to it here, on
  // the request that fed it (see lib/wallAllowance.ts).
  void userApi
    .recordWatchBatch(batch, keepalive)
    .then((r) => {
      if (typeof r.wallSeconds === "number") syncWallAllowance(r.wallSeconds);
    })
    .catch(() => undefined);
  return session;
}

function flushSession(keepalive: boolean): Promise<unknown> {
  if (sessionPending.size === 0) return Promise.resolve();
  const events = [...sessionPending.values()];
  sessionPending.clear();
  return api.session.watch(events, sessionDevice, keepalive).catch(() => undefined);
}

/** Bank every open interval up to now and carry on timing from here. */
function checkpoint(): void {
  const now = Date.now();
  for (const [slot, iv] of [...open]) {
    close(slot, now);
    open.set(slot, { ...iv, startedAt: now });
  }
}

/**
 * A session started or stopped on this screen. Banks what is open first, under the state
 * it ran in: the minutes before Start are not the session's, the minutes before Stop are.
 */
export function setWatchSession(on: boolean, deviceId?: string): void {
  sessionDevice = deviceId;
  if (on === sessionOn) return;
  checkpoint();
  sessionOn = on;
  void flush();
}

/** Send the session's seconds up to this instant, so a Stop right after finds them. */
export function flushWatchSession(): Promise<unknown> {
  checkpoint();
  return flush();
}

if (typeof window !== "undefined") {
  // pagehide, not visibilitychange: a wall TV reports hidden the moment its screensaver
  // kicks in and is still playing — the same reason clipViews and useRadio use pagehide.
  window.addEventListener("pagehide", () => {
    for (const slot of [...open.keys()]) close(slot, Date.now());
    flush(true);
  });
}

/** A new clip is on screen in `slot`: bank the previous interval, start this one. */
export function watchBegin(slot: string, clip: MediaItem): void {
  const now = Date.now();
  close(slot, now);
  parked.delete(slot);
  open.set(slot, { clip, startedAt: now, wall: onWall });
  // Fixed-cadence timer, started by the first open interval and not extended by later
  // ones — a busy wall flushes on a clock rather than never (clipViews' leading edge).
  timer ??= setTimeout(() => {
    timer = undefined;
    flush();
  }, FLUSH_MS);
}

/** `slot` is going away (its player unmounted): bank whatever it was watching. */
export function watchEnd(slot: string): void {
  close(slot, Date.now());
  parked.delete(slot);
}
